<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">

      <!-- Topbar -->
      <div class="wad-topbar">
        <button type="button" class="wad-back" @click="$router.back()">
          <i class="bi bi-arrow-left" />
          {{ $t('components.wirepasLocations.detail.backToList') }}
        </button>
        <div class="wad-range-pills">
          <button
            v-for="h in rangeOptions" :key="h.v" type="button" class="wl-pill"
            :class="{ 'wl-pill--active': rangeHours === h.v }"
            @click="setRange(h.v)"
          >{{ h.l }}</button>
        </div>
      </div>

      <div v-if="loading" class="wp-loading">
        <span class="spinner-border wp-loading__spinner" role="status" />
        <span class="wp-loading__text">{{ $t('components.wirepasLocations.detail.loading') }}</span>
      </div>

      <div v-else-if="error" class="alert alert-danger m-3">
        <i class="bi bi-exclamation-triangle-fill me-2" />{{ error }}
      </div>

      <template v-else-if="data">
        <!-- KPI -->
        <div class="wl-kpis mb-4">
          <div class="wl-stat wl-stat--dark">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasLocations.detail.kpi.total') }}</span>
              <i class="bi bi-collection wl-stat__icon" />
            </div>
            <span class="wl-stat__value">{{ data.assets.length }}</span>
          </div>
          <div class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasLocations.detail.kpi.active') }}</span>
              <i class="bi bi-activity wl-stat__icon" style="color:#3f8f52" />
            </div>
            <span class="wl-stat__value">{{ activeCount }}</span>
            <div class="wl-stat__accent" :style="{ width: data.assets.length ? `${(activeCount/data.assets.length)*100}%` : '0%', background: '#3f8f52' }" />
          </div>
          <div class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasLocations.detail.kpi.inactive') }}</span>
              <i class="bi bi-exclamation-triangle wl-stat__icon" style="color:#e8833a" />
            </div>
            <span class="wl-stat__value">{{ data.assets.length - activeCount }}</span>
          </div>
          <div class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasLocations.detail.kpi.gateways') }}</span>
              <i class="bi bi-broadcast-pin wl-stat__icon" :style="{ color: data.online ? '#3f8f52' : '#e8833a' }" />
            </div>
            <span class="wl-stat__value">{{ data.gateways.length }}</span>
          </div>
          <div v-if="data.address?.city" class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasLocations.detail.kpi.address') }}</span>
              <i class="bi bi-geo-alt wl-stat__icon" style="color:#f77f01" />
            </div>
            <span class="wl-stat__value" style="font-size:13px;font-weight:600">{{ data.address.city }}</span>
            <span v-if="data.address.street" class="wl-stat__meta">{{ data.address.street }}</span>
          </div>
        </div>

        <!-- Tabela assetów -->
        <div class="assets-grid-card mb-4">
          <div class="assets-grid-card__header">
            <div class="assets-grid-title">
              <div class="assets-grid-title__icon"><i class="bi bi-collection" /></div>
              <div>
                <div class="assets-grid-title__text">{{ data.name }}</div>
                <div class="text-muted small">
                  <span v-if="data.type" class="wirepas-type-badge me-2" :class="`wirepas-type-badge--${data.type}`">{{ locTypeLabel(data.type) }}</span>
                  {{ $t('components.wirepasLocations.detail.assetsSubtitle', { n: data.assets.length }) }}
                </div>
              </div>
            </div>
          </div>
          <div class="assets-grid-card__body p-3">
            <!-- Search + filters -->
            <div class="wl-assets-search-wrap mb-2">
              <input
                v-model="searchQ" type="text"
                class="form-control wl-assets-search"
                :placeholder="$t('components.wirepasLocations.detail.searchPlaceholder')"
              >
            </div>
            <div class="wl-assets-filters mb-3">
              <div class="wl-pills">
                <button
                  v-for="f in statusFilters" :key="f.v" type="button" class="wl-pill"
                  :class="{ 'wl-pill--active': statusFilter === f.v }"
                  @click="statusFilter = f.v"
                >{{ f.l }}</button>
              </div>
            </div>

            <div v-if="!filteredAssets.length" class="text-center text-muted py-3">
              {{ $t('components.wirepasLocations.detail.noAssets') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-sm wld-table">
                <thead>
                  <tr>
                    <th>{{ $t('components.wirepasAssets.columns.asset') }}</th>
                    <th>{{ $t('components.wirepasAssets.columns.lastSeen') }}</th>
                    <th>{{ $t('components.wirepasLocations.detail.temperature') }}</th>
                    <th>{{ $t('components.wirepasLocations.detail.humidity') }}</th>
                    <th>{{ $t('components.wirepasLocations.detail.battery') }}</th>
                    <th>{{ $t('components.wirepasLocations.detail.rssi') }}</th>
                    <th>{{ $t('components.wirepasAssets.columns.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="a in pagedAssets" :key="a.id"
                    class="wld-row"
                    @click="$router.push(`/wirepas-assets/${a.id}`)"
                  >
                    <td>
                      <span class="wl-asset-dot me-1" :class="a.active ? 'wl-asset-dot--active' : 'wl-asset-dot--inactive'" />
                      <span class="fw-semibold font-monospace">{{ a.name || a.serialNumber }}</span>
                      <span class="wl-category-badge ms-2">{{ a.assetCategory }}</span>
                    </td>
                    <td class="text-muted font-monospace small">{{ agoText(a.lastSeen) }}</td>
                    <td class="wld-value font-monospace small">{{ a.latest?.temperature != null ? a.latest.temperature.toFixed(1) + ' °C' : '—' }}</td>
                    <td class="wld-value font-monospace small">{{ a.latest?.humidity != null ? a.latest.humidity.toFixed(1) + ' %' : '—' }}</td>
                    <td class="wld-value font-monospace small">{{ a.latest?.battery != null ? (a.latest.battery / 1000).toFixed(2) + ' V' : '—' }}</td>
                    <td class="wld-value font-monospace small">{{ a.latest?.rssi != null ? a.latest.rssi.toFixed(1) + ' dBm' : '—' }}</td>
                    <td>
                      <span class="wl-status-badge" :class="`wl-status-badge--${a.status}`">
                        {{ $t(`components.wirepasAssets.status.${a.status}`) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Paginacja -->
              <div v-if="pageCount > 1" class="d-flex align-items-center justify-content-between mt-2 px-1">
                <span class="text-muted small">{{ filteredAssets.length }} {{ $t('components.wirepasLocations.detail.assetsTotal') }}</span>
                <div class="d-flex align-items-center gap-2">
                  <button class="wad-back" :disabled="page <= 1" @click="page--">‹</button>
                  <span class="text-muted small">{{ page }} / {{ pageCount }}</span>
                  <button class="wad-back" :disabled="page >= pageCount" @click="page++">›</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Wykresy per asset -->
        <div v-if="hasSeries" class="assets-grid-card">
          <div class="assets-grid-card__header">
            <div class="assets-grid-title">
              <div class="assets-grid-title__icon"><i class="bi bi-graph-up" /></div>
              <div>
                <div class="assets-grid-title__text">{{ $t('components.wirepasLocations.detail.charts') }}</div>
                <div class="text-muted small">{{ $t('components.wirepasLocations.detail.chartsSubtitle') }}</div>
              </div>
            </div>
          </div>
          <div class="assets-grid-card__body p-3 wad-charts row g-3">
            <div v-if="rssiSeries.datasets[0]?.data?.length" class="col-12 col-md-6 wad-chart-block">
              <div class="wad-chart-title"><i class="bi bi-reception-4" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.rssi') }} [dBm]</div>
              <LineChart :chart-data="rssiSeries" :options="chartOptions('dBm')" :height="160" />
            </div>
            <div v-if="tempSeries.datasets[0]?.data?.length" class="col-12 col-md-6 wad-chart-block">
              <div class="wad-chart-title"><i class="bi bi-thermometer-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.temperature') }} [°C]</div>
              <LineChart :chart-data="tempSeries" :options="chartOptions('°C')" :height="160" />
            </div>
            <div v-if="humSeries.datasets[0]?.data?.length" class="col-12 col-md-6 wad-chart-block">
              <div class="wad-chart-title"><i class="bi bi-droplet-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.humidity') }} [%]</div>
              <LineChart :chart-data="humSeries" :options="chartOptions('%')" :height="160" />
            </div>
            <div v-if="battSeries.datasets[0]?.data?.length" class="col-12 col-md-6 wad-chart-block">
              <div class="wad-chart-title"><i class="bi bi-battery-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.battery') }} [V]</div>
              <LineChart :chart-data="battSeries" :options="chartOptions('V')" :height="160" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { LineChart } from 'vue-chart-3'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'
import { getWirepasLocationDetail } from '@/crud/wirepas-location'
import { wirepasAgoText, wirepasStatusFilters } from '@/utils/wirepas'

Chart.register(...registerables, zoomPlugin)

const PALETTE = ['#f77f01', '#4870b8', '#3f8f52', '#d95f5f', '#a07c20', '#8f8f8c', '#403d58', '#a8cc6e']

const LOC_TYPE_LABELS: Record<string, Record<string, string>> = {
  pl: { hala: 'Hala', crossdock: 'Cross-dock', sklep: 'Sklep' },
  en: { hala: 'Hall', crossdock: 'Cross-dock', sklep: 'Store' },
}

export default defineComponent({
  name: 'WirepasLocationDetailView',
  components: { LineChart },
  emits: ['pageName'],

  setup(_, { emit }) {
    const { t, locale } = useI18n()
    const route = useRoute()

    const data = ref<any>(null)
    const loading = ref(true)
    const error = ref<string | null>(null)
    const rangeHours = ref(24)
    const searchQ = ref('')
    const statusFilter = ref<'all' | 'ok' | 'low_batt' | 'in_transit' | 'departed'>('all')
    const page = ref(1)
    const pageSize = 20

    const isDarkMode = ref(document.body.classList.contains('dark-theme'))
    let darkModeObserver: MutationObserver | null = null

    const rangeOptions = [
      { v: 6, l: '6h' }, { v: 24, l: '24h' }, { v: 48, l: '48h' }, { v: 168, l: '7d' },
    ]

    const statusFilters = computed(() => wirepasStatusFilters(t))

    const fetchData = async () => {
      loading.value = true
      error.value = null
      try {
        const res = await getWirepasLocationDetail(route.params.id as string, rangeHours.value)
        data.value = res.data
        emit('pageName', data.value.name)
      } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || t('components.wirepasLocations.detail.error')
      } finally {
        loading.value = false
      }
    }

    const setRange = (h: number) => { rangeHours.value = h; fetchData() }
    const agoText = (ts: number | null) => wirepasAgoText(ts, t)
    const locTypeLabel = (type: string) => LOC_TYPE_LABELS[locale.value]?.[type] ?? LOC_TYPE_LABELS['pl'][type] ?? type

    const activeCount = computed(() => (data.value?.assets ?? []).filter((a: any) => a.active).length)

    const filteredAssets = computed(() => {
      const q = searchQ.value.toLowerCase().trim()
      return (data.value?.assets ?? []).filter((a: any) => {
        if (statusFilter.value !== 'all' && a.status !== statusFilter.value) return false
        if (!q) return true
        return (a.serialNumber ?? '').toLowerCase().includes(q) || (a.name ?? '').toLowerCase().includes(q)
      })
    })

    watch([searchQ, statusFilter], () => { page.value = 1 })

    const pageCount = computed(() => Math.max(1, Math.ceil(filteredAssets.value.length / pageSize)))
    const pagedAssets = computed(() => {
      const p = Math.min(page.value, pageCount.value)
      return filteredAssets.value.slice((p - 1) * pageSize, p * pageSize)
    })

    // Build multi-series chart data — one series per asset
    const buildSeries = (paramKey: string, unit: string) => {
      const datasets: any[] = []
      const series: Record<string, any[]> = data.value?.series ?? {}
      ;(data.value?.assets ?? []).forEach((asset: any, idx: number) => {
        const pts = series[String(asset.id)]?.[paramKey]
        if (!pts?.length) return
        const scale = paramKey === 'battery' ? 1000 : 1
        datasets.push({
          label: asset.name || asset.serialNumber,
          data: pts.map((p: any) => ({ x: p.ts, y: p.v / scale })),
          borderColor: PALETTE[idx % PALETTE.length],
          backgroundColor: PALETTE[idx % PALETTE.length] + '18',
          borderWidth: 1.5,
          pointRadius: 0,
          fill: false,
          tension: 0,
        })
      })
      return { datasets }
    }

    const tempSeries = computed(() => buildSeries('temperature', '°C'))
    const humSeries  = computed(() => buildSeries('humidity', '%'))
    const battSeries = computed(() => buildSeries('battery', 'V'))
    const rssiSeries = computed(() => buildSeries('rssi', 'dBm'))

    const hasSeries = computed(() =>
      tempSeries.value.datasets.length || humSeries.value.datasets.length ||
      battSeries.value.datasets.length || rssiSeries.value.datasets.length
    )

    const chartOptions = (unit: string) => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: {
          type: 'time' as const,
          time: { tooltipFormat: 'DD/MM/YYYY HH:mm', displayFormats: { minute: 'HH:mm', hour: 'DD/MM HH:mm', day: 'DD/MM' } },
          grid: { color: isDarkMode.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
          ticks: { font: { size: 11 }, color: isDarkMode.value ? 'rgba(255,255,255,0.6)' : undefined },
        },
        y: {
          grid: { color: isDarkMode.value ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
          ticks: {
            font: { size: 11 },
            color: isDarkMode.value ? 'rgba(255,255,255,0.6)' : undefined,
            callback: (v: any) => `${typeof v === 'number' ? v.toFixed(unit === 'V' ? 2 : 1) : v}${unit}`,
          },
        },
      },
      plugins: {
        // A 'customBackground' plugin registered globally by the vehicle report charts (Voltage.vue
        // etc.) paints a fixed light background on every Chart.js instance in the app, dark mode or
        // not. Opt this chart out rather than touching that shared, unrelated plugin.
        customBackground: false,
        decimation: { enabled: true, algorithm: 'lttb' as const, samples: 500 },
        legend: {
          display: true, position: 'bottom' as const,
          labels: { font: { size: 11 }, usePointStyle: true, padding: 10, color: isDarkMode.value ? 'rgba(255,255,255,0.7)' : undefined },
        },
        tooltip: { mode: 'index' as const, intersect: false },
        zoom: {
          pan: { enabled: true, mode: 'x' as const },
          zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const },
        },
      },
    })

    watch(() => route.params.id, fetchData)
    onMounted(() => {
      emit('pageName', '…')
      fetchData()

      darkModeObserver = new MutationObserver(() => {
        isDarkMode.value = document.body.classList.contains('dark-theme')
      })
      darkModeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    })
    onUnmounted(() => darkModeObserver?.disconnect())

    return {
      data, loading, error, rangeHours, rangeOptions, statusFilters,
      searchQ, statusFilter, page, pageCount, pagedAssets,
      activeCount, filteredAssets, hasSeries,
      tempSeries, humSeries, battSeries, rssiSeries,
      setRange, agoText, locTypeLabel, chartOptions,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

.wad-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 0 16px;
  flex-wrap: wrap;
}

.wad-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover:not(:disabled) { border-color: var(--primary-color); color: var(--primary-color); }
  &:disabled { opacity: 0.4; cursor: default; }
}

.wad-range-pills { display: flex; gap: 6px; flex-wrap: wrap; }

// Layout comes from the Bootstrap .row.g-3 classes on this element in the template —
// two charts per row on md+ screens, one per row on narrower ones.

.wad-chart-block {}

.wad-chart-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(108, 117, 125, 0.9);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.wld-table {
  font-size: 13px;

  // Bootstrap's --bs-table-color default isn't wired into this app's dark-theme override,
  // so these cells would otherwise inherit a light-mode-only grey and go low-contrast on dark.
  .wld-value { color: var(--text-color, #252422); }

  // .table-hover paints row backgrounds via this custom property, hardcoded light (#F5F8FA) by
  // Bootstrap's own default and never overridden for dark mode — hover made every row nearly
  // white with unreadable text. Route it through the app's own hover token instead.
  --bs-table-hover-bg: var(--card-bg-hover, rgba(0, 0, 0, 0.035));

  thead th {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #888;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    white-space: nowrap;
  }
}

// Hover background comes from --bs-table-hover-bg set on .wld-table above — Bootstrap's
// .table-hover targets the <td> children directly, so a background here would never show.
.wld-row { cursor: pointer; }

.wl-pills { display: flex; gap: 6px; flex-wrap: wrap; }

.wl-pill {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: rgba(var(--primary-color-rgb), 0.4); color: var(--primary-color); }
  &--active { background: var(--primary-color); border-color: var(--primary-color); color: #fff; }
}

.wl-assets-search-wrap .wl-assets-search {
  max-width: 360px;
  border-radius: 10px;
  font-size: 13px;
}
</style>
